import React, { useState, useContext, useMemo } from "react";
import { Header } from "us.collection.admin/layouts/Bureau/Header";
import { matchPath, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BoxIcons, IconTypes } from "us.icons";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { ConfigurationContext } from "us.common/ConfigurationContext/ConfigurationProvider";
import { getSelectedMenuKeyByModule } from "us.collection/functions";
import { $Tooltip } from "us.common/components";

const { $Layout, $Menu } = Common.Components;
const { Content, Sider } = $Layout;

const Home: React.FC = (props) => {
  const { t } = useTranslation(["US.COLLECTION.LAYOUT", "US.COLLECTION.ADMIN"]);
  const context = useContext(ConfigurationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const { params }: any = matchPath(
    {
      path: "/bureau/:module?",
      end: false,
      caseSensitive: false,
    },
    location.pathname
  );

  const bureauPermission = useMemo(() => {
    return context.componentPermission["bureau"];
  }, [context]);

  const navigateTo = (url: string): void => {
    window.scrollTo(0, 0);
    navigate({ ...location, pathname: `/bureau/${url}` }, { state: location.state });
  };

  const onCollapse = (collapsed: boolean): void => {
    setIsCollapsed(collapsed);
  };

  const menuItemAvalibility = (menuItemName: string): boolean => {
    try {
      return bureauPermission?.routes[menuItemName]?.isEnabled;
    } catch (error) {
      return false;
    }
  };

  return (
    <$Layout style={{ minHeight: "100vh" }}>
      <Header {...props} />
      <$Layout>
        <Content className="mt-0">
          <Outlet />
        </Content>
        <Sider
          collapsible
          collapsed={isCollapsed}
          onCollapse={(e) => onCollapse(e)}
          reverseArrow={true}
        >
          <$Menu
            theme="dark"
            mode="inline"
            selectedKeys={getSelectedMenuKeyByModule(params["module"])}
          >
            {menuItemAvalibility("routinesAndActivities") && (
              <$Menu.Item
                onClick={() => navigateTo("history")}
                key="16"
                data-testid="bureau-layout-routinesAndActivities"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="activity-history" />
                <span>{t("US.COLLECTION.LAYOUT:CASE.HISTORY")}</span>
              </$Menu.Item>
            )}
            {menuItemAvalibility("manageCreditorGroups") && (
              <$Menu.Item
                key="11"
                onClick={() => navigateTo("manage-creditor-groups")}
                data-testid="bureau-layout-creditorGroups"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="creditor-groups" />
                <$Tooltip
                  placement="top"
                  title={t(
                    "US.COLLECTION.LAYOUT:BUREAU.MANAGE_CREDITOR_GROUPS"
                  )}
                >
                  <span>
                    {t("US.COLLECTION.LAYOUT:BUREAU.MANAGE_CREDITOR_GROUPS")}
                  </span>
                </$Tooltip>
              </$Menu.Item>
            )}
            {menuItemAvalibility("bmd") && (
              <$Menu.Item
                key="3"
                onClick={() => navigateTo("bmd")}
                data-testid="bureau-layout-bmd"
              >
                <BoxIcons type={IconTypes.BOX_ICON} name="bmd" />
                <span>{t("US.COLLECTION.LAYOUT:BUREAU.BMD")}</span>
              </$Menu.Item>
            )}
            <$Menu.Item
              key="4"
              onClick={() => navigateTo("workflows")}
              data-testid="bureau-layout-bmd"
            >
              <BoxIcons type={IconTypes.BOX_ICON} name="bureau-workflows" />
              <span>{t("US.COLLECTION.LAYOUT:BUREAU.WORKFLOWS")}</span>
            </$Menu.Item>
            <$Menu.Item
              key="5"
              onClick={() => navigateTo("workflows-troubleshoot")}
            >
              <BoxIcons
                type={IconTypes.BOX_ICON}
                name="workflows-troubleshoot"
              />
              <span>
                {t("US.COLLECTION.LAYOUT:BUREAU.WORKFLOWS_TROUBLESHOOT")}
              </span>
            </$Menu.Item>
          </$Menu>
        </Sider>
      </$Layout>
    </$Layout>
  );
};

export default Home;
